/// <reference types="youtube" preserve="true" />
import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from "@angular/core";
import { ExternalLinkVideoPlayerComponent } from "../external-link-video-player";
import {
  AutoHide,
  AutoPlay,
  Controls,
  FullscreenButton,
  IvLoadPolicy,
  KeyboardControls,
  Mute,
  PlayerState,
  RelatedVideos,
  YTPlayerError
} from "./youtube-player";
import { mediaReadyDelay } from "../../cfs-template";
import { YoutubePlayerService } from "./youtube-player.service";
import { take } from "rxjs/operators";
import { TranslocoModule } from "@ngneat/transloco";
import { RelativeLoaderComponent } from "../../relative-loader";
import { provideClearlineCommonTranslocoScope } from "../../../utils";
import { BehaviorSubject } from "rxjs";
import {
  VideoPlayerErrorMessages,
  VideoPlayerErrorTitles,
  VideoPlayerErrorTypes
} from "../../cfs-template/video-template/video-player-error-messages";
import {
  CfsTemplateErrorDataFields,
  CfsTemplateErrorTypes,
  generateCfsTemplateError,
  generateYoutubePlayerErrorMessage
} from "../../cfs-template/cfs-template-errors";
import { CfsTemplateError, CfsTemplateErrorData } from "../../../models";

interface ThumbnailImageInfo {
  url?: string;
  loaded?: boolean;
}

export const youtubeUrlPattern =
  /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[?&]\S*)?$/;

@Component({
  selector: "lib-youtube-player",
  templateUrl: "./youtube-player.component.html",
  styleUrls: ["./youtube-player.component.scss"],
  standalone: true,
  imports: [CommonModule, TranslocoModule, RelativeLoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideClearlineCommonTranslocoScope()]
})
export class YoutubePlayerComponent extends ExternalLinkVideoPlayerComponent implements OnDestroy {
  @ViewChild("player", { static: true }) playerElement: ElementRef | undefined;
  @Input() templateName = "";

  protected readonly urlPattern = youtubeUrlPattern;
  protected readonly playerId: string = `youtube-player-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

  protected override player: YT.Player | null = null;
  protected videoId = "";
  protected playerState: PlayerState = PlayerState.UNSTARTED;
  protected playerStateKey$ = new BehaviorSubject("UNSTARTED");
  protected thumbnailImageInfo$ = new BehaviorSubject<ThumbnailImageInfo>({ url: "", loaded: false });
  protected displayThumbnailOverlay$ = new BehaviorSubject(false);
  protected displayUploadPlaceholder$ = new BehaviorSubject(true);

  private readonly GENERIC_THUMBNAIL_DIMENSIONS = {
    SMALL_PLACEHOLDER: { width: 120, height: 90 },
    MEDIUM_PLACEHOLDER: { width: 320, height: 180 }
  };
  private readonly thumbnailOverlayDisplayDelay = 300;
  private displayThumbnailOverlayTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private service: YoutubePlayerService, private zone: NgZone, private changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    if (this.playerElement?.nativeElement) {
      this.playerElement.nativeElement.id = this.playerId;
    }
    this.updateOverlays();
  }

  async ngOnDestroy() {
    this.isDestroyInProgress = true;

    this.clearThumbnailOverlayTimeout();
    await this.resetPlayer();
  }

  play(): void {
    if (this.isPlayerActive() && this.player?.playVideo) {
      this.player?.playVideo();
    }
  }

  pause(): void {
    if (this.isPlayerActive() && this.player?.pauseVideo) {
      this.player?.pauseVideo();
    }
  }

  stop(): void {
    if (this.isPlayerActive() && this.player?.stopVideo) {
      this.player?.stopVideo();
    }
  }

  restart(): void {
    if (this.isPlayerActive() && this.player?.seekTo) {
      this.player?.seekTo(0, true);
      this.play();
    }
  }

  getVideoDuration(): Promise<number> {
    if (!this.isPlayerActive() || !this.player?.getDuration) {
      return Promise.resolve(0);
    }

    return Promise.resolve(this.player?.getDuration() || 0);
  }

  getThumbnailUrl(): Promise<string> {
    return this.getThumbnailUrlById(this.videoId);
  }

  protected loadPlayer(): void {
    if (this.videoId) {
      this.service.onApiReady$.pipe(take(1)).subscribe(() => this.createPlayer());
    } else {
      const errorData: CfsTemplateErrorData = { field: CfsTemplateErrorDataFields.Id, value: this.videoId };
      const errorMessage: string = generateYoutubePlayerErrorMessage(VideoPlayerErrorTypes.RequiredId);
      const templateError: CfsTemplateError = generateCfsTemplateError(VideoPlayerErrorTypes.RequiredId, errorMessage, errorData);

      this.setError(templateError);
      void this.resetPlayer();
    }
  }

  protected onThumbnailImageLoad(): void {
    this.thumbnailImageInfo$.next({ ...this.thumbnailImageInfo$.value, loaded: true });
  }

  protected getVideoIdByUrl(url: string): string {
    const match = url.match(this.urlPattern) || [];

    return match.length ? match[1] : "";
  }

  private createPlayer(): void {
    const { muted = true, autoplay = false } = this.mediaConfig || {};

    this.clearSettings();
    this.player?.destroy();
    this.updateOverlays();

    this.zone.runOutsideAngular(() => {
      this.player = new YT.Player(this.playerElement?.nativeElement, {
        videoId: this.videoId,
        playerVars: {
          autohide: AutoHide.HideAllControls,
          autoplay: autoplay ? AutoPlay.AutoPlay : AutoPlay.NoAutoPlay,
          controls: Controls.Hide,
          disablekb: KeyboardControls.Disable,
          fs: FullscreenButton.Hide,
          iv_load_policy: IvLoadPolicy.Hide,
          mute: muted ? Mute.Muted : Mute.NotMuted,
          rel: RelatedVideos.Hide,
          playsinline: 1,
          cc_load_policy: 0,
          modestbranding: 1,
          enablejsapi: 1
        },
        events: {
          onError: this.onPlayerError.bind(this),
          onReady: this.onPlayerReady.bind(this),
          onStateChange: this.onPlayerStateChange.bind(this)
        }
      });
      this.loaded = true;

      this.updateThumbnailImageInfo(this.videoId);
      this.updateOverlays();
    });
  }

  private updateThumbnailImageInfo(videoId: string): void {
    this.getThumbnailUrlById(videoId).then((thumbnailUrl: string) => {
      this.thumbnailImageInfo$.next({ url: thumbnailUrl, loaded: false });
    });
  }

  private async resetPlayer(): Promise<void> {
    console.log("Vimeo player reset", !!this.player); // todo: remove logs after testing
    if (this.player) {
      await this.player.destroy();
      this.player = null;
    }

    return Promise.resolve();
  }

  private onPlayerReady(event: YT.PlayerEvent): void {
    console.log("Youtube player ready", event); // todo: remove logs after testing
    const iframeElement: HTMLIFrameElement | null = this.player?.getIframe() || null;

    if (iframeElement) {
      iframeElement.style.pointerEvents = "none";
    }

    setTimeout(() => {
      this.ready.emit();
    }, mediaReadyDelay);
  }

  private onPlayerStateChange(event: YT.OnStateChangeEvent): void {
    if (event.data === PlayerState.ENDED && this.mediaConfig?.loop) {
      this.player?.seekTo(0, true);
      this.player?.playVideo();
    }
    this.setPlayerState(event.data);
  }

  private onPlayerError(event: YT.OnErrorEvent): void {
    const playerError: YTPlayerError = event.data as unknown as YTPlayerError;
    const errorType: string = this.getErrorType(playerError);
    const errorMessage = this.generateErrorMessage(playerError);
    const errorData: CfsTemplateErrorData = { field: CfsTemplateErrorDataFields.Url, value: this.videoUrl };
    const templateError: CfsTemplateError = generateCfsTemplateError(errorType, errorMessage, errorData);

    this.thumbnailImageInfo$.next({ url: "", loaded: false });
    this.setError(templateError);
    this.clearSettings();
    this.updateOverlays();
  }

  private generateErrorMessage(playerError: YTPlayerError): string {
    switch (playerError) {
      case YTPlayerError.InvalidParam:
        return `${VideoPlayerErrorTitles.Youtube}: The request contained an invalid parameter value`;
      case YTPlayerError.Html5Error:
        return `${VideoPlayerErrorTitles.Youtube}: The requested content cannot be played in an HTML5 player`;
      case YTPlayerError.VideoNotFound:
        return `${VideoPlayerErrorTitles.Youtube}: The video requested was not found`;
      case YTPlayerError.EmbeddingNotAllowed:
        return `${VideoPlayerErrorTitles.Youtube}: The owner of the requested video does not allow it to be played in embedded players`;
      case YTPlayerError.EmbeddingNotAllowed2:
        return `${VideoPlayerErrorTitles.Youtube}: This error is the same as 101. It's just a 101 error in disguise!`;
      default:
        return `${VideoPlayerErrorTitles.Youtube}: ${VideoPlayerErrorMessages.Unknown}`;
    }
  }

  private getErrorType(value: YTPlayerError): string {
    const key = Object.keys(YTPlayerError).find((k) => YTPlayerError[k as keyof typeof YTPlayerError] === value);
    return key || CfsTemplateErrorTypes.VideoLoad;
  }

  private clearSettings(): void {
    this.setPlayerState(PlayerState.UNSTARTED);
    this.thumbnailImageInfo$.next({ url: "", loaded: false });
  }

  private setPlayerState(playerState: PlayerState = PlayerState.UNSTARTED): void {
    this.playerState = playerState;
    const playerStateKey = Object.keys(PlayerState).find((key) => PlayerState[key as keyof typeof PlayerState] === this.playerState);

    this.playerStateKey$.next(playerStateKey as string);
    console.log(`YouTube Player ${this.templateName} state change:`, playerStateKey);
    console.log(`YouTube Player ${this.templateName} loaded fraction: `, this.player?.getVideoLoadedFraction()); // todo: remove logs after testing
    this.updateOverlays();
  }

  private updateOverlays(): void {
    this.updateDisplayThumbnailOverlay();
    this.updateDisplayUploadPlaceholder();
    this.changeDetectorRef.detectChanges();
  }

  private updateDisplayThumbnailOverlay(): void {
    const shouldDisplayThumbnailOverlay: boolean =
      !!this.videoId && !!this.player && this.loaded && this.playerState !== PlayerState.PLAYING && !this.playerErrorOccurred;

    if (this.displayThumbnailOverlay$.value && !shouldDisplayThumbnailOverlay) {
      this.clearThumbnailOverlayTimeout();

      this.displayThumbnailOverlayTimeoutId = setTimeout(() => {
        this.displayThumbnailOverlay$.next(false);
        this.displayThumbnailOverlayTimeoutId = null;
      }, this.thumbnailOverlayDisplayDelay);
    } else if (!this.displayThumbnailOverlay$.value && shouldDisplayThumbnailOverlay) {
      this.clearThumbnailOverlayTimeout();
      this.displayThumbnailOverlay$.next(true);
    }
  }

  private updateDisplayUploadPlaceholder(): void {
    const displayUploadPlaceholder: boolean =
      this.displayUploadPlaceholder &&
      (!this.videoId || !this.loaded || (!this.thumbnailImageInfo$.value.loaded && this.displayThumbnailOverlay$.value)) &&
      !this.playerErrorOccurred;

    this.displayUploadPlaceholder$.next(displayUploadPlaceholder);
  }

  private clearThumbnailOverlayTimeout(): void {
    if (this.displayThumbnailOverlayTimeoutId !== null) {
      clearTimeout(this.displayThumbnailOverlayTimeoutId);
      this.displayThumbnailOverlayTimeoutId = null;
    }
  }

  private async getThumbnailUrlById(videoId = ""): Promise<string> {
    if (!videoId || !this.player) {
      return "";
    }

    const maxResUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    const isMaxResGeneric = await this.checkIfThumbnailIsGeneric(maxResUrl);

    if (!isMaxResGeneric) {
      return maxResUrl;
    }

    const hqUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    const isHqGeneric = await this.checkIfThumbnailIsGeneric(hqUrl);

    if (!isHqGeneric) {
      return hqUrl;
    }

    // todo: consider creating our own custom YouTube thumbnail if needed
    return "";
  }

  private checkIfThumbnailIsGeneric(imageUrl: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        const isGenericPlaceholder = Object.values(this.GENERIC_THUMBNAIL_DIMENSIONS).some(
          (dimensions) => img.width === dimensions.width && img.height === dimensions.height
        );
        resolve(isGenericPlaceholder);
      };

      img.onerror = () => resolve(true);

      img.src = imageUrl;
    });
  }
}
